import PrintLayout from "components/Dashboard/DoctorDashboard/notes-and-forms/components/print-layout/PrintLayout";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import queryMetaData from "services/queryMetaData";
import querySecureData from "services/querySecureData";
import { orchid_decode_key, orchid_decrypt } from "utils/OrchidEncryption";
import styles from "./FormPrintOrDownloadTrigger.module.scss";
import { usePrintOptionsModal } from "../print-options-modal/PrintOptionsModal";

type Props = {
  partialData?: Partial<FormMetaData>;
  action: "download" | "print";
  children: React.ReactNode;
};

// Usage: Wrap the element you want to trigger the download or print with this component
const FormPrintOrDownloadTrigger = ({
  partialData: patrialData,
  action,
  children,
}: Props) => {
  const [isShotingPrintLayout, setIsShowingPrintLayout] =
    useState<boolean>(false);
  const [metaData, setMetaData] = useState<Partial<NoteMetaData>>();
  const [data, setData] = useState<Record<string, any>>();

  const formDataId = patrialData?.form_data_id || "";

  const { openPrintOptionsModal, printOptions, printOptionsModal } =
    usePrintOptionsModal({
      onConfirm: () => setIsShowingPrintLayout(true),
      action,
    });

  const handlePrintOrDownload = useCallback(async () => {
    if (!formDataId) {
      console.warn("No form data id provided");
      return;
    }

    openPrintOptionsModal();

    const metaData = await queryMetaData({
      type: "form",
      id: formDataId,
    });
    setMetaData(metaData);
    if (metaData?.cipher_key) {
      const decodeKey = await orchid_decode_key(metaData?.cipher_key);

      let nextSecureData: Record<string, any> = {};
      try {
        nextSecureData = await querySecureData({
          type: "form",
          id: formDataId,
        });
      } catch (error) {
        /**
         * swallow the error
         * @see https://orchidhealth.atlassian.net/browse/OIM-765
         */
      }

      const data = nextSecureData?.data
        ? JSON.parse(
            (await orchid_decrypt(nextSecureData.data, decodeKey)) || "{}",
          )
        : {};

      setData(data);
    }
  }, [formDataId, openPrintOptionsModal]);

  return (
    <>
      <div onClick={handlePrintOrDownload}>{children}</div>

      {isShotingPrintLayout &&
        metaData &&
        data &&
        createPortal(
          <div
            className={styles["generate-note-pdf"]}
            aria-hidden="true"
            tabIndex={-1}
          >
            <PrintLayout
              type="form"
              action={action}
              onAction={() => setIsShowingPrintLayout(false)}
              metaData={metaData as any} // Logic copied from FormFillIn, the data type doesn't match
              data={data}
              templateContentOptions={printOptions}
            />
          </div>,
          document.body,
        )}

      {printOptionsModal}
    </>
  );
};

export default FormPrintOrDownloadTrigger;
